<template>
    <div
        class="font-body relative flex max-w-md min-w-[320px] flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
    >
        <!-- Countdown Timer in top right corner -->
        <div class="absolute top-4 right-4">
            <div class="relative h-8 w-8">
                <svg
                    class="h-8 w-8 -rotate-90 transform"
                    viewBox="0 0 36 36"
                >
                    <circle
                        class="text-gray-200"
                        stroke-width="3"
                        stroke="currentColor"
                        fill="transparent"
                        r="15.915"
                        cx="18"
                        cy="18"
                    />
                    <circle
                        class="text-brand transition-all duration-1000"
                        stroke-width="3"
                        stroke="currentColor"
                        fill="transparent"
                        stroke-linecap="round"
                        r="15.915"
                        cx="18"
                        cy="18"
                        :stroke-dasharray="circumference"
                        :stroke-dashoffset="circumference - (timeRemaining / 30) * circumference"
                    />
                </svg>
                <div class="absolute inset-0 flex items-center justify-center">
                    <span class="text-xs font-semibold text-gray-700">{{ timeRemaining }}</span>
                </div>
            </div>
        </div>

        <!-- Header with Phone Icon -->
        <div class="flex items-center gap-3">
            <SvgIcon
                name="system-phone"
                class="text-brand h-8 w-8 shrink-0"
            />
            <div class="text-muval-gray-1 font-semibold">Incoming call</div>
        </div>

        <!-- Content with Labels in Columns -->
        <div class="mx-2 grid grid-cols-[1fr_2fr] gap-x-4 gap-y-2">
            <div class="text-muval-gray-2 text-xs font-medium">Phone</div>
            <div class="text-muval-gray-1 text-sm">{{ formattedPhoneNumber || phoneNumber }}</div>
            <template v-if="customerName">
                <div class="text-muval-gray-2 text-xs font-medium">Customer</div>
                <div class="text-muval-gray-1 text-sm">{{ customerName }}</div>
            </template>
            <template v-if="moveType">
                <div class="text-muval-gray-2 text-xs font-medium">Move</div>
                <div class="text-muval-gray-1 text-sm">{{ moveType }} move</div>
            </template>
            <template v-if="orderRef">
                <div class="text-muval-gray-2 text-xs font-medium">Order</div>
                <div class="text-muval-gray-1 text-sm">#{{ orderRef }}</div>
            </template>
        </div>

        <!-- Action Buttons -->
        <div class="mt-1 flex flex-row-reverse gap-2">
            <MuvalButton
                v-if="orderId"
                small
                primary
                @click="openOrder"
            >
                View order
            </MuvalButton>
            <MuvalButton
                small
                tertiary
                @click.stop="dismiss"
            >
                Ignore
            </MuvalButton>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { toast as toaster } from 'vue-sonner';
import { useNotificationsStore } from '@store/notifications';
import MuvalButton from '@components/button/MuvalButton.vue';
import SvgIcon from '@components/utilities/SvgIcon.vue';

const props = defineProps({
    phoneNumber: {
        type: String,
        required: true,
    },
    customerName: {
        type: String,
        default: null,
    },
    orderId: {
        type: Number,
        default: null,
    },
    orderRef: {
        type: String,
        default: null,
    },
    moveType: {
        type: String,
        default: null,
    },
    inboxCallId: {
        type: Number,
        default: null,
    },
});

const router = useRouter();
const notificationsStore = useNotificationsStore();
const timeRemaining = ref(30);
const timerInterval = ref(null);

const circumference = 2 * Math.PI * 15.915; // radius is 15.915

const formattedPhoneNumber = computed(() => {
    if (!props.phoneNumber) {
        return '';
    }

    let phone = props.phoneNumber;

    // Handle 1300/1800 service numbers in E.164 format (+611300... / +611800...)
    const serviceMatch = phone.match(/^\+61(1[38]00)(\d{3})(\d{3})$/);
    if (serviceMatch) {
        return `${serviceMatch[1]} ${serviceMatch[2]} ${serviceMatch[3]}`;
    }

    // Non-AU international callers — AU national formatting doesn't apply
    if (phone.startsWith('+') && !phone.startsWith('+61')) {
        return props.phoneNumber;
    }

    // Handle E.164 format (+61...) for mobile/landline
    if (phone.startsWith('+61')) {
        phone = '0' + phone.substring(3);
    }

    if (phone.length >= 10) {
        if (phone.substring(0, 2) === '04') {
            return `${phone.substring(0, 4)} ${phone.substring(4, 7)} ${phone.substring(7, 10)}`;
        } else if (phone.length >= 10) {
            return `${phone.substring(0, 2)} ${phone.substring(2, 6)} ${phone.substring(6, 10)}`;
        }
    }

    return props.phoneNumber;
});

function startCountdown() {
    timerInterval.value = setInterval(() => {
        timeRemaining.value--;

        if (timeRemaining.value <= 0) {
            dismiss();
        }
    }, 1000);
}

function dismiss() {
    if (timerInterval.value) {
        clearInterval(timerInterval.value);
        timerInterval.value = null;
    }
    // Look up the toast id from the store using inbox_call_id and dismiss by id.
    if (props.inboxCallId) {
        const key = `inbox_call_${props.inboxCallId}`;
        const toastData = notificationsStore.incomingCallToasts[key];
        if (toastData && toastData.id !== null && toastData.id !== undefined) {
            toaster.dismiss(toastData.id);
            delete notificationsStore.incomingCallToasts[key];
        }
    }
    // No `else`: without an inbox_call_id we cannot target this call's toast,
    // and dismissing *all* toasts (toaster.dismiss() with no id) would wipe
    // unrelated notifications (leads, AI inventory, etc.). Leave it to expire
    // on its own 30s duration rather than nuking the whole stack.
}

function openOrder() {
    const id = props.orderId;
    if (id != null && id !== 'undefined') {
        router.push({ name: 'OrderView', params: { id } });
        dismiss();
    }
}

onMounted(() => {
    startCountdown();
});

onUnmounted(() => {
    if (timerInterval.value) {
        clearInterval(timerInterval.value);
    }
});
</script>
